import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Text, TextStyle, Animated, View, StyleProp, Easing, ScrollView, StyleSheet, ViewStyle } from 'react-native';
import Flex from './atom/Flex';

// 来回滚动播放组件
export function ScrollSwipper({
    style,
    children,
    scrollBack = false,
}: React.PropsWithChildren<{ style?: ViewStyle; direction?: 'horizontal' | 'vertical'; scrollBack?: boolean }>) {
    const [containerSize, setContainerSize] = useState(0);
    const [contentSize, setContentSize] = useState(0);
    const moveAnim = useRef(new Animated.Value(0)).current; // 初始位置
    const [diffSize, setDiffSize] = useState(0);
    const duration = useRef(1000);
    const animation = useRef(null as Animated.CompositeAnimation);

    useEffect(() => {
        if (diffSize > 0) {
            duration.current = Math.floor((scrollBack ? diffSize : contentSize) / 20) * 1000;
            animation.current = Animated.loop(
                Animated.sequence([
                    Animated.timing(moveAnim, {
                        toValue: scrollBack ? -diffSize : -contentSize,
                        duration: duration.current,
                        useNativeDriver: true,
                    }),
                    Animated.timing(moveAnim, {
                        toValue: scrollBack ? 10 : containerSize,
                        duration: scrollBack ? duration.current : 0,
                        useNativeDriver: true,
                    }),
                ])
            );
            animation.current.start();
        } else {
            animation.current?.stop?.();
            moveAnim.setValue(0);
        }
    }, [diffSize, moveAnim, containerSize, contentSize, scrollBack]);

    useEffect(() => {
        console.log('contentSize', contentSize, 'containerSize', containerSize);
        if (contentSize && containerSize) setDiffSize(contentSize - containerSize);
    }, [contentSize, containerSize]);

    return (
        <Flex
            style={{"flex":0,"overflow":"hidden","position":"relative"}}
            onLayout={e => {
                setContainerSize(e.nativeEvent.layout.width);
            }}>
            <ScrollView horizontal={true} showsHorizontalScrollIndicator={false} showsVerticalScrollIndicator={false} scrollEnabled={false}>
                <Animated.View
                    style={[
                        style,
                        {
                            transform: [{ translateX: moveAnim }],
                            width: '100%',
                        },
                    ]}
                    onLayout={e => {
                        const layout = e.nativeEvent.layout;
                        console.log('layout.height', layout.height);
                        setContentSize(layout.width);
                    }}>
                    {typeof children === 'string' ? <Text>{children}</Text> : children}
                </Animated.View>
            </ScrollView>
        </Flex>
    );
}

// 循环滚动播放组件
export function LoopScrollSwipper({
    style,
    children,
}: React.PropsWithChildren<{ style?: StyleProp<View>; direction?: 'horizontal' | 'vertical'; scrollBack?: boolean }>) {
    const [containerSize, setContainerSize] = useState(0);
    const [contentSize, setContentSize] = useState(0);
    const moveAnim = useRef(new Animated.Value(0)).current; // 初始位置
    const [diffSize, setDiffSize] = useState(0);
    const duration = useRef(1000);
    const animation = useRef(null as Animated.CompositeAnimation);

    useEffect(() => {
        if (diffSize > 0) {
            duration.current = Math.floor(containerSize / 30) * 1000;
            animation.current = Animated.loop(
                Animated.sequence([
                    Animated.timing(moveAnim, {
                        toValue: -contentSize,
                        duration: duration.current,
                        useNativeDriver: true,
                        easing: Easing.linear,
                    }),
                    Animated.timing(moveAnim, {
                        toValue: 0,
                        duration: 0,
                        useNativeDriver: true,
                    }),
                ])
            );
            animation.current.start();
        } else {
            animation.current?.stop?.();
            moveAnim.setValue(0);
        }
    }, [diffSize, moveAnim, containerSize, contentSize]);

    useEffect(() => {
        if (contentSize && containerSize) setDiffSize(contentSize - containerSize);
    }, [contentSize, containerSize]);

    return (
        <Flex
            style={{"flex":0,"overflow":"hidden"}}
            onLayout={e => {
                setContainerSize(e.nativeEvent.layout.width);
            }}>
            <Animated.View
                style={[
                    style,
                    {
                        transform: [{ translateX: moveAnim }],
                        flexDirection: 'row',
                    },
                ]}
                onLayout={e => {
                    // 说明已经初始化了
                    if (diffSize > 0) return;
                    setContentSize(e.nativeEvent.layout.width);
                }}>
                {typeof children === 'string' ? <Text>{children}</Text> : children}
                {diffSize > 0 ? typeof children === 'string' ? <Text>{children}</Text> : children : null}
            </Animated.View>
        </Flex>
    );
}

export default function TextSwipper({
    style,
    children,
    speed = 67,
    step = 2,
}: React.PropsWithChildren<{
    style?: TextStyle;
    speed?: number;
    step?: number;
}>) {
    const [posX, setPosX] = useState(0);
    const [boxW, setBoxW] = useState(0);
    const [textW, setTextW] = useState(0);
    const [sholdStart, setSholdStart] = useState(false);
    useEffect(() => {
        if (boxW && textW && textW >= boxW - 4) {
            setSholdStart(true);
        } else {
            setSholdStart(false);
            setPosX(0);
        }
    }, [boxW, textW]);
    useEffect(() => {
        if (!sholdStart) return;
        const timer = setInterval(() => {
            setPosX(posX > textW ? 0 : posX + step);
        }, speed);
        return () => clearInterval(timer);
    }, [posX, textW, sholdStart]);

    const posStyle = useMemo(
        () =>
            StyleSheet.create({
                default: {
                    transform: [{ translateX: -posX }],
                },
            }),
        [posX]
    );
    return (
        <ScrollView
            horizontal={true}
            showsHorizontalScrollIndicator={false}
            onLayout={e => {
                setBoxW(e.nativeEvent.layout.width);
            }}>
            <Flex style={{"flex":1,"overflow":"hidden"}}>
                <Text
                    style={[style, posStyle.default]}
                    onLayout={e => {
                        setTextW(e.nativeEvent.layout.width);
                    }}>
                    {children}
                </Text>
            </Flex>
        </ScrollView>
    );
}
